import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  fetchPostsApi,
  handleAdminPostApi,
  approveUncheckByCategory,
} from '../../../../api/admin/post.api';
import { CheckCheck, Check } from 'lucide-react';
import { AdminButton, AdminPageHeader } from '../../ui';
import { AdminPostModCard } from '../post-moderation/AdminPostParts';
import { promptTotp } from '../../../../utils/admin-stepup';

// Danh mục "Hoài niệm (Cab cũ)" — bài phục dựng cho trang /old.
const HOAI_NIEM_CATEGORY_ID = '01dcab00-0000-0000-0000-000000000001';
// Gỡ đuôi thương hiệu còn sót trong caption bài cũ (giống trang /old).
const cleanCap = (s?: string) =>
  (s ?? '').split(/\s*[|\-–]\s*cab\.vn/i)[0].trim();

const ManagementArchivePost = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const { data } = await fetchPostsApi(page, 'Uncheck', HOAI_NIEM_CATEGORY_ID);
      const list = (data?.data ?? []).map((p: any) => ({
        ...p,
        content: cleanCap(p.content),
        title: cleanCap(p.title),
      }));
      setPosts(list);
      setHasMore(!!data?.hasMore);
    } catch {
      toast.error('Không tải được danh sách bài Cab xưa');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(pageNumber);
  }, [pageNumber, load]);

  const handlePost = async (postId: string, status: string, reason?: string) => {
    const totp = promptTotp('duyệt bài viết');
    if (!totp) return;
    try {
      await handleAdminPostApi(postId, status, reason, totp);
      toast.success('Thành công');
      // bỏ bài vừa xử khỏi danh sách hiện tại (khỏi tải lại cả trang)
      setPosts((prev) => prev.filter((p) => p.id !== postId));
    } catch {
      toast.error('Thất bại');
    }
  };

  const handleReject = async (postId: string) => {
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Nhập lý do loại bài này:')?.trim();
    if (!reason) return;
    await handlePost(postId, 'Rejected', reason);
  };

  const handleApproveAll = async () => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        'Duyệt CÔNG KHAI toàn bộ bài Cab xưa đang chờ duyệt? Thao tác này áp cho tất cả bài (không chỉ trang này).'
      )
    )
      return;
    const totp = promptTotp('duyệt hàng loạt bài Cab xưa');
    if (!totp) return;
    setBulkLoading(true);
    try {
      const { data } = await approveUncheckByCategory(HOAI_NIEM_CATEGORY_ID, 'Public', undefined, totp);
      toast.success(`Đã duyệt công khai ${data} bài Cab xưa`);
      setPageNumber(1);
      await load(1);
    } catch {
      toast.error('Duyệt hàng loạt thất bại');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Duyệt bài Cab xưa"
        description="Bài phục dựng 2011–2014 chờ duyệt để hiển thị trên trang /old."
        actions={
          <AdminButton
            variant="success"
            icon={<CheckCheck size={18} />}
            onClick={handleApproveAll}
            loading={bulkLoading}
          >
            {bulkLoading ? 'Đang duyệt...' : 'Duyệt tất cả'}
          </AdminButton>
        }
      />

      {loading && posts.length === 0 ? (
        <p className="text-center py-10" style={{ color: 'var(--adm-muted)' }}>Đang tải...</p>
      ) : posts.length === 0 ? (
        <p className="text-center py-10" style={{ color: 'var(--adm-muted)' }}>
          Không còn bài Cab xưa nào chờ duyệt
        </p>
      ) : (
        <div className="adm-mod-grid">
          {posts.map((post, index) => (
            <AdminPostModCard
              key={post.id || index}
              post={post}
              actions={
                <>
                  <AdminButton size={32} variant="danger" onClick={() => handleReject(post.id)}>
                    Loại bỏ
                  </AdminButton>
                  <AdminButton size={32} variant="success" icon={<Check size={14} />} onClick={() => handlePost(post.id, 'Public')}>
                    Công khai
                  </AdminButton>
                </>
              }
            />
          ))}
        </div>
      )}

      {/* Phân trang: bấm trang nào gọi trang đó */}
      <div className="adm-pagination" style={{ justifyContent: 'center' }}>
        <button
          type="button"
          className="adm-page-btn adm-page-btn--nav"
          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          disabled={pageNumber <= 1 || loading}
        >
          Trang trước
        </button>
        <span className="adm-page-btn adm-page-btn--active">{pageNumber}</span>
        <button
          type="button"
          className="adm-page-btn adm-page-btn--nav"
          onClick={() => setPageNumber((p) => p + 1)}
          disabled={!hasMore || loading}
        >
          Trang sau
        </button>
      </div>
    </div>
  );
};

export default ManagementArchivePost;
